import { useEffect, useRef } from "react";
import { deckClearState, deckPutState, deckWaitIntent, type DeckAction, type DeckStatePayload } from "./api";

// The console side of the Stream Deck bridge (ADR 0024, studio-grade ADR 0096).
// While enabled, the tab (Director OR Legend) publishes its rich state (so the
// plugin paints keys + dials) and long-polls for presses/rotations (so they drive
// the show). The Stream Deck plugin — which runs inside the Elgato app, coexisting
// with it — is the other half; it talks to the API on its own. The on-screen
// controls stay authoritative, so the bridge failing never blocks anything.

export type DeckBridgeState = DeckStatePayload;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Run the console deck bridge while `enabled`. `onAction` receives the encoded
 * intent (ADR 0096) for each press / dial event — the tab parses + executes it,
 * and remains the source of truth for its own state.
 */
export function useDeckBridge(
  enabled: boolean,
  state: DeckBridgeState,
  onAction: (action: DeckAction) => void,
): void {
  // Latest values without re-subscribing the long-poll loop each render.
  const onActionRef = useRef(onAction);
  onActionRef.current = onAction;
  const stateRef = useRef(state);
  stateRef.current = state;
  const stateKey = JSON.stringify(state);

  // Publish state whenever it changes (and on enable, to register presence).
  useEffect(() => {
    if (!enabled) return;
    void deckPutState(stateRef.current).catch(() => undefined);
  }, [enabled, stateKey]);

  // Long-poll for key presses. Stable across state changes; torn down on
  // disable/unmount (which also clears the bridge row).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      // Seed the cursor from the current seq so a stale press never replays.
      let since = 0;
      try {
        const seeded = await deckPutState(stateRef.current);
        since = seeded.seq;
      } catch {
        // fall through — start from 0
      }
      while (!cancelled) {
        const started = Date.now();
        try {
          const intent = await deckWaitIntent(since, ctrl.signal);
          if (cancelled) break;
          if (intent.seq > since) {
            since = intent.seq;
            if (intent.action) onActionRef.current(intent.action);
          } else if (Date.now() - started < 1000) {
            // The server should hold the wait open; if it returned instantly with
            // no new press (misconfig/proxy), throttle so we never hot-loop.
            await sleep(1000);
          }
        } catch {
          if (cancelled || ctrl.signal.aborted) break;
          await sleep(1000); // transient error — back off, then keep waiting
        }
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
      void deckClearState();
    };
  }, [enabled]);
}
